import PropTypes from 'prop-types';
// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';

// project imports
import DrawerHeaderStyled from './DrawerHeaderStyled';

import useConfig from 'hooks/useConfig';
import { MenuOrientation } from 'config';
import BrandedLogo from '../../../../components/logo/BrandedLogo';
import { useLogos } from '../../../../services/logoService';

// ==============================|| DRAWER HEADER ||============================== //

export default function DrawerHeader({ open }) {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const taLogo = open ? 'logo1-big.png' : 'logo1-small.png';
  const isuLogo = open ? 'logo2-big.png' : 'logo2-small.png';
  const logoVariant = open ? 'big' : 'small';
  const logoWidth = open ? 'auto' : '18px';

  const { menuOrientation } = useConfig();
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // Fork-join: both logos fetched in parallel.
  // Because login preloaded ALL four variants, `loading` will almost always
  // be false instantly here (SWR cache hit — no loader flash).
  const { urls, loading } = useLogos([taLogo, isuLogo]);

  const taUrl = urls[taLogo];
  const isuUrl = urls[isuLogo];

  const showDivider = !loading && !!taUrl && !!isuUrl;

  return (
    <DrawerHeaderStyled
      open={open}
      sx={{
        minHeight: isHorizontal ? 'unset' : '60px',
        width: isHorizontal ? { xs: '100%', lg: '424px' } : 'initial',
        paddingTop: isHorizontal ? { xs: '20px', lg: '0' } : '8px',
        paddingBottom: isHorizontal ? { xs: '20px', lg: '0' } : '8px',
        paddingLeft: isHorizontal ? { xs: '20px', lg: '0' } : open ? '20px' : 0,
        paddingRight: isHorizontal ? { xs: '20px', lg: '0' } : open ? '20px' : 0,
        marginLeft: open ? 0 : 1,
        marginRight: open ? 0 : 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={open ? 1.5 : 0.75}
        sx={{ width: '100%', position: 'relative', minHeight: open ? 35 : 18 }}
      >
        {/* Single rectangular skeleton — visible while fork-join is in-flight.
            After login the cache is warm so this almost never appears in the sidebar. */}
        {loading && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            width={open ? 200 : 20}
            height={open ? 35 : 20}
            sx={{ borderRadius: open ? '4px' : '50%' }}
          />
        )}

        {/* Logo 1 */}
        {!loading && !!taUrl && (
          <BrandedLogo
            name={taLogo}
            variant={logoVariant}
            sx={{ width: logoWidth, height: open ? 35 : 18, maxWidth: open ? '100px' : 'auto' }}
            hideLoader
          />
        )}

        {/* Divider */}
        {showDivider && (
          <Divider
            orientation="vertical"
            sx={{ borderRightWidth: 1, borderColor: '#DEDEDE', height: open ? 22 : 10, alignSelf: 'center' }}
          />
        )}

        {/* Logo 2 */}
        {!loading && !!isuUrl && (
          <BrandedLogo
            name={isuLogo}
            variant={logoVariant}
            sx={{ width: logoWidth, height: open ? 35 : 18, maxWidth: open ? '100px' : 'auto' }}
            hideLoader
          />
        )}
      </Stack>
    </DrawerHeaderStyled>
  );
}

DrawerHeader.propTypes = { open: PropTypes.bool };
